import { io } from 'socket.io-client';

/**
 * VoiceSocketClient
 * Socket.IO client for voice AI sessions.
 * Connects to the /voice namespace on the server.
 */
export class VoiceSocketClient {
  constructor() {
    this.socket = null;
    this.handlers = {};
  }

  /**
   * Connect to the voice server
   * @param {object} options
   * @param {string} [options.token] - JWT token (staff auth)
   * @param {string} [options.voiceToken] - Voice token (patient auth)
   * @param {string} [options.url] - Server URL (defaults to window.location)
   */
  connect({ token, voiceToken, url } = {}) {
    // In dev, CRA runs on :3000 but the API/Socket.IO server is on :3001
    const serverUrl = url || window.location.origin.replace(':3000', ':3001');

    this.socket = io(`${serverUrl}/voice`, {
      auth: { token, voiceToken },
      transports: ['websocket'],
      reconnection: false
    });

    // Register event forwarding
    const events = [
      'audioReady', 'audioOutput', 'textOutput',
      'toolUse', 'toolResult', 'sessionClosed', 'error',
      'connect_error'
    ];

    for (const event of events) {
      this.socket.on(event, (data) => {
        if (this.handlers[event]) {
          this.handlers[event](data);
        }
      });
    }

    this.socket.on('connect', () => {
      console.log('[VoiceSocket] Connected');
      if (this.handlers.connect) this.handlers.connect();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[VoiceSocket] Disconnected:', reason);
      if (this.handlers.disconnect) this.handlers.disconnect(reason);
    });
  }

  /**
   * Register event handler
   * @param {string} event
   * @param {Function} handler
   */
  on(event, handler) {
    this.handlers[event] = handler;
    return this;
  }

  /**
   * Initialize a voice session
   * @param {object} data - { practiceId, patientId, campaignType }
   */
  initializeConnection(data) {
    if (!this.socket) { console.warn('[VoiceSocket] initializeConnection called but not connected'); return; }
    this.socket.emit('initializeConnection', data);
  }

  /**
   * Start the Bedrock prompt
   */
  promptStart() {
    if (!this.socket) { console.warn('[VoiceSocket] promptStart called but not connected'); return; }
    this.socket.emit('promptStart');
  }

  /**
   * Signal start of audio stream
   */
  startAudio() {
    if (!this.socket) { console.warn('[VoiceSocket] startAudio called but not connected'); return; }
    this.socket.emit('audioStart');
  }

  /**
   * Send audio chunk
   * @param {string} base64Audio
   */
  sendAudio(base64Audio) {
    if (!this.socket) return;
    this.socket.emit('audioInput', { audio: base64Audio });
  }

  /**
   * Stop audio stream
   */
  stopAudio() {
    if (!this.socket) return;
    this.socket.emit('stopAudio');
  }

  /**
   * End the voice session
   */
  endSession() {
    if (!this.socket) return;
    this.socket.emit('endSession');
  }

  /**
   * Disconnect
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.handlers = {};
  }

  /**
   * Check if connected
   */
  get isConnected() {
    return this.socket?.connected ?? false;
  }
}

export default VoiceSocketClient;
